import { createTheme } from '@mui/material/styles';

const primaryBgImportant = (theme) => `${theme.palette.primary.main} !important`;

/** Teal + white hover highlight for custom list rows (popovers, category pickers, sales item rows, etc.) */
export const interactiveListHoverSx = {
  transition: 'background-color 0.12s ease, color 0.12s ease',
  '&:hover': {
    backgroundColor: 'primary.main',
    color: '#ffffff',
    '& .MuiTableCell-root': {
      backgroundColor: primaryBgImportant,
    },
    '& .MuiTypography-root, & .MuiTableCell-root, & .MuiSvgIcon-root, & svg': {
      color: '#ffffff !important',
    },
    '& .MuiLink-root': {
      color: '#ffffff !important',
      bgcolor: 'transparent !important',
      backgroundColor: 'transparent !important',
      boxShadow: 'none !important',
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
    },
    '& .MuiInput-input, & .MuiInputBase-input, & input, & textarea': {
      color: '#ffffff !important',
      WebkitTextFillColor: '#ffffff !important',
    },
  },
};

/** Active / keyboard-focus state matching list hover */
export const interactiveListActiveSx = {
  backgroundColor: 'primary.main',
  color: '#ffffff',
  '& .MuiTableCell-root': {
    backgroundColor: primaryBgImportant,
  },
  '& .MuiTypography-root, & .MuiTableCell-root, & .MuiSvgIcon-root, & svg': {
    color: '#ffffff !important',
  },
  '& .MuiInput-input, & .MuiInputBase-input, & input, & textarea': {
    color: '#ffffff !important',
    WebkitTextFillColor: '#ffffff !important',
  },
};

/** Collapsed "See more" over clamped description — white fade; transparent on teal row hover */
export const salesItemDescriptionSeeMoreLinkCollapsedSx = (theme) => ({
  position: 'absolute',
  right: 10,
  bottom: 0,
  display: 'inline',
  pr: 0.75,
  bgcolor: theme.palette.background.paper,
  lineHeight: 1.43,
  boxShadow: `-10px 0 10px -2px ${theme.palette.background.paper}`,
  '.MuiTableRow-root:hover &': {
    bgcolor: 'transparent',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    color: '#ffffff',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
});

export const SALES_FORM_SERIAL_SUBROW_CLASS = 'sales-form-serial-subrow';

/** Row hover for sales document item tables — beats forced white cell backgrounds on tbody cells */
export const salesFormItemsTableRowHoverSx = {
  '& .MuiTableBody-root .MuiTableRow-root:not(.sales-form-serial-subrow):hover': {
    backgroundColor: 'primary.main',
    '& .MuiTableCell-root': {
      backgroundColor: primaryBgImportant,
      color: '#ffffff !important',
    },
    '& .MuiTypography-root, & .MuiSvgIcon-root, & svg': {
      color: '#ffffff !important',
    },
    '& .MuiLink-root': {
      color: '#ffffff !important',
      bgcolor: 'transparent !important',
      backgroundColor: 'transparent !important',
      boxShadow: 'none !important',
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
    },
    '& .MuiInput-input, & .MuiInputBase-input, & input, & textarea': {
      color: '#ffffff !important',
      WebkitTextFillColor: '#ffffff !important',
    },
  },
};

const listOptionHoverStyles = (theme) => ({
  backgroundColor: `${theme.palette.primary.main} !important`,
  color: `${theme.palette.primary.contrastText} !important`,
  '& .MuiTypography-root, & .MuiListItemIcon-root, & .MuiSvgIcon-root, & svg': {
    color: `${theme.palette.primary.contrastText} !important`,
  },
});

const theme = createTheme({
  palette: {
    primary: {
      main: '#105e7a',
      light: '#1a7f9e',
      dark: '#0a4a5e',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0d47a1',
      light: '#5472d3',
      dark: '#002171',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 500,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          transition: 'background-color 0.12s ease, color 0.12s ease',
          '&:hover, &.Mui-focusVisible': listOptionHoverStyles(theme),
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
            '& .MuiListItemIcon-root, & .MuiSvgIcon-root, & svg': {
              color: theme.palette.primary.contrastText,
            },
            '&:hover, &.Mui-focusVisible': listOptionHoverStyles(theme),
          },
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: ({ theme }) => ({
          transition: 'background-color 0.12s ease, color 0.12s ease',
          '&:hover, &.Mui-focused': listOptionHoverStyles(theme),
          '&[aria-selected="true"]': {
            backgroundColor: `${theme.palette.primary.light} !important`,
            color: `${theme.palette.primary.contrastText} !important`,
            '&:hover, &.Mui-focused': listOptionHoverStyles(theme),
          },
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          transition: 'background-color 0.12s ease, color 0.12s ease',
          '&:hover': listOptionHoverStyles(theme),
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
            '&:hover': listOptionHoverStyles(theme),
          },
        }),
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.MuiListItem-button:hover': listOptionHoverStyles(theme),
        }),
      },
    },
  },
});

export default theme; 